import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Group,
  Button,
  TextInput,
  Select,
  Switch,
  CloseButton,
  Popover,
  Stack,
  Badge,
  SimpleGrid,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Search, Plus, Download, Upload, Filter } from 'react-feather';
import type { ColumnConfig } from '../../../../hooks';
import { ColumnSettingsPopover } from '../../../../components/common';
import type { ParticipantsFilterState } from './useParticipantsData';

export interface ParticipantsFiltersProps {
  filters: ParticipantsFilterState;
  /** Available group options (id + name) for the group filter dropdown */
  groupOptions: { value: string; label: string }[];
  /** Available personality types for the personality type dropdown */
  personalityTypes: string[];
  /** Column config for the settings popover */
  columnConfig: ColumnConfig[];
  /** Column config change handler */
  onColumnsChange: (columns: ColumnConfig[]) => void;
  /** Open the add custom column modal */
  onAddCustomColumn: () => void;
  /** Delete a custom column */
  onDeleteCustomColumn: (fieldId: string) => void;
  /** Open the export modal */
  onExport: () => void;
  /** Open the add participant modal */
  onAddParticipant: () => void;
  /** Open the upload CSV modal */
  onUploadCSV?: () => void;
  /** Portal targets for rendering into the sub-header bar */
  portalTargetLeft?: HTMLElement | null;
  portalTargetRight?: HTMLElement | null;
}

export function ParticipantsFilters({
  filters,
  groupOptions,
  personalityTypes,
  columnConfig,
  onColumnsChange,
  onAddCustomColumn,
  onDeleteCustomColumn,
  onExport,
  onAddParticipant,
  onUploadCSV,
  portalTargetLeft,
  portalTargetRight,
}: ParticipantsFiltersProps) {
  const {
    searchValue,
    setSearchValue,
    statusFilter,
    setStatusFilter,
    groupFilter,
    setGroupFilter,
    assignedFilter,
    setAssignedFilter,
    personalityFilter,
    setPersonalityFilter,
    intakeFilter,
    setIntakeFilter,
    flaggedOnly,
    setFlaggedOnly,
    hasActiveFilters,
    clearFilters,
  } = filters;

  const [popoverOpened, { toggle: togglePopover, close: closePopover }] = useDisclosure(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (groupFilter !== 'all') count++;
    if (assignedFilter !== 'all') count++;
    if (personalityFilter !== 'all') count++;
    if (intakeFilter !== 'all') count++;
    return count;
  }, [statusFilter, groupFilter, assignedFilter, personalityFilter, intakeFilter]);

  const leftContent = (
    <>
      <TextInput
        placeholder="Search participants..."
        leftSection={<Search size={16} />}
        rightSection={searchValue ? (
          <CloseButton size="sm" onClick={() => setSearchValue('')} />
        ) : undefined}
        value={searchValue}
        onChange={(e) => setSearchValue(e.currentTarget.value)}
        w={280}
      />
      <Popover
        opened={popoverOpened}
        onClose={closePopover}
        position="bottom-start"
        width={480}
        shadow="md"
      >
        <Popover.Target>
          <Button
            variant="outline"
            color="gray"
            bg="white"
            style={{ borderColor: '#E8E6E3' }}
            leftSection={<Filter size={16} />}
            rightSection={activeFilterCount > 0 ? (
              <Badge size="xs" circle color="medleyGreen" variant="filled">
                {activeFilterCount}
              </Badge>
            ) : undefined}
            onClick={togglePopover}
          >
            Filters
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="md">
            <SimpleGrid cols={2} spacing="md">
              <Select
                label="Status"
                data={[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active' },
                  { value: 'withdrawn', label: 'Withdrawn' },
                ]}
                value={statusFilter}
                onChange={(value) => setStatusFilter((value || 'all') as typeof statusFilter)}
                clearable={false}
              />
              <Select
                label="Group"
                data={[
                  { value: 'all', label: 'All Groups' },
                  ...groupOptions,
                ]}
                value={groupFilter}
                onChange={(value) => setGroupFilter(value || 'all')}
                clearable={false}
              />
              <Select
                label="Assignment"
                data={[
                  { value: 'all', label: 'All Participants' },
                  { value: 'assigned', label: 'Assigned' },
                  { value: 'unassigned', label: 'Unassigned' },
                ]}
                value={assignedFilter}
                onChange={(value) => setAssignedFilter((value || 'all') as typeof assignedFilter)}
                clearable={false}
              />
              <Select
                label="Intake"
                data={[
                  { value: 'all', label: 'All Intake' },
                  { value: 'not_sent', label: 'Not Sent' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'completed', label: 'Completed' },
                ]}
                value={intakeFilter}
                onChange={(value) => setIntakeFilter((value || 'all') as typeof intakeFilter)}
                clearable={false}
              />
            </SimpleGrid>
            {personalityTypes.length > 0 && (
              <Select
                label="Type"
                data={[
                  { value: 'all', label: 'All Types' },
                  ...personalityTypes.map((type) => ({ value: type, label: type })),
                ]}
                value={personalityFilter}
                onChange={(value) => setPersonalityFilter(value || 'all')}
                clearable={false}
              />
            )}
            <Switch
              label="Show flagged only"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.currentTarget.checked)}
            />
            {hasActiveFilters && (
              <>
                <Divider />
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={clearFilters}
                  fullWidth
                >
                  Clear all filters
                </Button>
              </>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </>
  );

  const rightContent = (
    <>
      <ColumnSettingsPopover
        columns={columnConfig}
        onColumnsChange={onColumnsChange}
        onAddCustomColumn={onAddCustomColumn}
        onDeleteCustomColumn={onDeleteCustomColumn}
      />
      <Button variant="outline" color="gray" bg="white" style={{ borderColor: '#E8E6E3' }} leftSection={<Download size={16} />} onClick={onExport}>
        Export
      </Button>
      {onUploadCSV && (
        <Button variant="outline" color="gray" bg="white" style={{ borderColor: '#E8E6E3' }} leftSection={<Upload size={16} />} onClick={onUploadCSV}>
          Upload CSV
        </Button>
      )}
      <Button leftSection={<Plus size={16} />} onClick={onAddParticipant}>
        Add Participant
      </Button>
    </>
  );

  // Portal mode: render into sub-header targets
  if (portalTargetLeft && portalTargetRight) {
    return (
      <>
        {createPortal(leftContent, portalTargetLeft)}
        {createPortal(rightContent, portalTargetRight)}
      </>
    );
  }

  // Fallback: inline rendering
  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="sm" wrap="nowrap">{leftContent}</Group>
      <Group gap="sm" wrap="nowrap">{rightContent}</Group>
    </Group>
  );
}
